import { useState, useEffect, useCallback } from "react";
import { jobsService } from "@/api/services/jobs.service";
import { jobToUpdateDto } from "@/utils/jobUtils";
export function useJob(jobId) {
    const [state, setState] = useState({
        job: null,
        loading: true,
        saving: false,
        error: null,
    });
    const fetchJob = useCallback(async () => {
        if (!jobId) {
            setState((prev) => ({
                ...prev,
                loading: false,
                error: "No job ID provided",
            }));
            return;
        }
        setState((prev) => ({ ...prev, loading: true, error: null }));
        try {
            const response = await jobsService.getJob(jobId);
            setState({
                job: response.data,
                loading: false,
                saving: false,
                error: null,
            });
        }
        catch (err) {
            const errorMessage = err instanceof Error ? err.message : "Failed to load job";
            setState((prev) => ({
                ...prev,
                loading: false,
                error: errorMessage,
            }));
        }
    }, [jobId]);
    const updateJob = useCallback(async (data) => {
        if (!jobId)
            return null;
        setState((prev) => ({ ...prev, saving: true, error: null }));
        try {
            const payload = state.job ? jobToUpdateDto(state.job, data) : data;
            const response = await jobsService.updateJob(jobId, payload);
            setState((prev) => ({
                ...prev,
                job: response.data,
                saving: false,
            }));
            return response.data;
        }
        catch (err) {
            const errorMessage = err instanceof Error ? err.message : "Failed to save job";
            setState((prev) => ({
                ...prev,
                saving: false,
                error: errorMessage,
            }));
            return null;
        }
    }, [jobId, state.job]);
    const updateTradeAssignment = useCallback(async (id, data) => {
        setState((prev) => ({ ...prev, saving: true, error: null }));
        try {
            const response = await jobsService.updateTradeAssignment(id, data);
            setState((prev) => {
                const job = prev.job;
                if (!job?.tradeAssignments)
                    return { ...prev, saving: false };
                const updated = job.tradeAssignments.map((a) => a.id === response.data.id ? response.data : a);
                return {
                    ...prev,
                    job: { ...job, tradeAssignments: updated },
                    saving: false,
                };
            });
            await fetchJob();
            return response.data;
        }
        catch (err) {
            const errorMessage = err instanceof Error
                ? err.message
                : "Failed to update trade assignment";
            setState((prev) => ({
                ...prev,
                saving: false,
                error: errorMessage,
            }));
            return null;
        }
    }, [fetchJob]);
    const processDateChangeRequest = useCallback(async (id, data) => {
        setState((prev) => ({ ...prev, saving: true, error: null }));
        try {
            const response = await jobsService.processDateChangeRequest(id, data);
            // Refresh job to get updated dates
            await fetchJob();
            return response.data;
        }
        catch (err) {
            const errorMessage = err instanceof Error
                ? err.message
                : "Failed to process date change request";
            setState((prev) => ({
                ...prev,
                saving: false,
                error: errorMessage,
            }));
            return null;
        }
    }, [fetchJob]);
    useEffect(() => {
        fetchJob();
    }, [fetchJob]);
    return {
        ...state,
        fetchJob,
        updateJob,
        updateTradeAssignment,
        processDateChangeRequest,
        refetch: fetchJob,
    };
}
